import React, { useEffect, useState } from 'react';
import { EmailAuthProvider, multiFactor, reauthenticateWithCredential, TotpMultiFactorGenerator } from 'firebase/auth';
import { httpsCallable } from 'firebase/functions';
import { functions } from '../firebase.config';
import { UserAuth } from '../context/AuthContext';

export default function AccountSecurity({ selectedUid, onAccountChanged, enrollmentOnly = false }) {
  const { user, isAdmin } = UserAuth();
  const [uid, setUid] = useState('');
  const [reason, setReason] = useState('');
  const [message, setMessage] = useState('');
  const [busy, setBusy] = useState(false);
  const [password, setPassword] = useState('');
  const [secret, setSecret] = useState(null);
  const [code, setCode] = useState('');
  useEffect(() => { if (selectedUid) setUid(selectedUid); }, [selectedUid]);
  if (!user || (!isAdmin && !enrollmentOnly)) return null;
  const execute = async action => {
    if (busy) return;
    setBusy(true); setMessage('');
    try { await action(); }
    catch (error) { setMessage(error.message || 'The operation could not be completed.'); }
    finally { setBusy(false); }
  };
  const block = blocked => execute(async () => {
    if (!uid.trim() || reason.trim().length < 3) { setMessage('Enter the user UID and a reason.'); return; }
    if (!window.confirm(`${blocked ? 'Block' : 'Unblock'} user ${uid.trim()}?`)) return;
    await httpsCallable(functions, 'setAccountBlocked')({ uid: uid.trim(), blocked, reason: reason.trim() });
    onAccountChanged?.();
    setMessage(blocked ? 'Account blocked and sessions revoked.' : 'Account unblocked. The user must sign in again.');
  });
  const beginEnrollment = () => execute(async () => {
    if (!user.emailVerified) { setMessage('Verify your email first.'); return; }
    await reauthenticateWithCredential(user, EmailAuthProvider.credential(user.email, password));
    setPassword('');
    setSecret(await TotpMultiFactorGenerator.generateSecret(await multiFactor(user).getSession()));
  });
  const finishEnrollment = () => execute(async () => {
    await multiFactor(user).enroll(TotpMultiFactorGenerator.assertionForEnrollment(secret, code), 'Authenticator');
    setSecret(null); setCode('');
    setMessage(enrollmentOnly ? 'Authenticator enrolled. Sign out and sign in with your authenticator code, then let Ben know you are ready for administrator access.' : 'Authenticator enrolled. Sign out and sign in with your authenticator code before enabling the administrator requirement.');
  });
  const inputClass = 'mt-2 w-full rounded border border-white/20 bg-[#12181B] p-3 text-white';
  return <section className="my-6 rounded-xl border border-white/20 bg-[#16181e] p-6 text-white">
    <h2 className="text-lg font-semibold">Administrator security</h2>
    <div className="mt-5 space-y-8">
      <div>
        <h3 className="font-semibold">Authenticator protection</h3>
        {process.env.REACT_APP_TOTP_ENABLED !== 'true' ? <p className="mt-3 text-sm text-white/70">Authenticator enrollment will be available once Identity Platform is enabled for this site.</p> : <>
          <p className="mt-2 text-sm text-white/70">Set up your authenticator to protect administrator access.</p>
          {!secret ? <>
            <label className="mt-3 block text-sm">Confirm your password<input type="password" autoComplete="current-password" className={inputClass} value={password} onChange={e => setPassword(e.target.value)} /></label>
            <button disabled={busy || !password || multiFactor(user).enrolledFactors.length > 0} onClick={beginEnrollment} className="mt-3 rounded border border-white/30 px-4 py-2 disabled:opacity-50">Set up authenticator</button>
          </> : <>
            <p className="mt-3 text-sm">In your authenticator app, add a time-based account using this setup key. Keep it private.</p>
            <code className="mt-2 block break-all rounded bg-black p-3">{secret.secretKey}</code>
            <label className="mt-3 block text-sm">Authenticator code<input inputMode="numeric" autoComplete="one-time-code" className={inputClass} value={code} onChange={e => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))} /></label>
            <button disabled={busy || code.length !== 6} onClick={finishEnrollment} className="mt-3 rounded border border-white/30 px-4 py-2 disabled:opacity-50">Confirm authenticator</button>
          </>}
          {!enrollmentOnly && <button disabled={busy} onClick={() => execute(async () => {
            if (!window.confirm('Require authenticator sign-in for all administrators? Every administrator must enroll first.')) return;
            await httpsCallable(functions, 'enableAdminMfaRequirement')({});
            setMessage('Authenticator sign-in is now required for administrator access.');
          })} className="mt-3 block rounded bg-[#35d99a] px-4 py-2 text-black disabled:opacity-50">Require MFA for administrators</button>}
        </>}
      </div>
      {!enrollmentOnly && <div>
        <h3 className="font-semibold">Block an account</h3>
        <label className="mt-3 block text-sm">User UID<input className={inputClass} value={uid} onChange={e => setUid(e.target.value)} maxLength={128} /></label>
        <label className="mt-3 block text-sm">Reason<input className={inputClass} value={reason} onChange={e => setReason(e.target.value)} maxLength={500} /></label>
        <div className="mt-3 flex gap-3">
          <button disabled={busy} onClick={() => block(true)} className="rounded bg-red-600 px-4 py-2 disabled:opacity-50">Block user</button>
          <button disabled={busy} onClick={() => block(false)} className="rounded border border-white/30 px-4 py-2 disabled:opacity-50">Unblock user</button>
        </div>
      </div>}
    </div>
    {message && <p role="status" className="mt-4 text-sm">{message}</p>}
  </section>;
}
